import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LogOut, ShieldCheck, Users, Bell, ChevronRight, BadgeCheck, Camera, User, GraduationCap, Plus, X } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { formatDate } from '@/lib/format';
import { Card, TierBadge, StatusBadge, Progress } from '@/components/shared';
import PushToggle from '@/components/PushToggle';

const readFileDataUrl = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = (e) => resolve(e.target.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

// Resize an image data URL to fit within max px, return JPEG data URL
const resizeDataUrl = (dataUrl, max = 1000, quality = 0.82) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => {
    let { width, height } = img;
    if (width > max || height > max) {
      if (width >= height) { height = Math.round(height * (max / width)); width = max; }
      else { width = Math.round(width * (max / height)); height = max; }
    }
    const canvas = document.createElement('canvas');
    canvas.width = width; canvas.height = height;
    canvas.getContext('2d').drawImage(img, 0, 0, width, height);
    resolve(canvas.toDataURL('image/jpeg', quality));
  };
  img.onerror = () => resolve(dataUrl);
  img.src = dataUrl;
});

const MAX_PORTFOLIO = 8;

const COMPLETE_FIELDS = ['name', 'phone', 'city', 'gender', 'maritalStatus', 'birthDate', 'region', 'university', 'department', 'iban', 'verification', 'profilePhoto'];

export default function Profile() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const fileRef = useRef(null);
  const portfolioRef = useRef(null);
  const [arch, setArch] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [uploadingPortfolio, setUploadingPortfolio] = useState(false);

  useEffect(() => {
    api.get('/architect/me').then((r) => {
      const a = r.data.architect;
      setArch(a);
      setForm({
        name: a.name, phone: a.phone, city: a.city, iban: a.iban, verification: a.verification,
        gender: a.gender || '', maritalStatus: a.maritalStatus || '', birthDate: a.birthDate || '',
        region: a.region || '', university: a.university || '', department: a.department || '',
        profilePhoto: a.profilePhoto || '', portfolio: a.portfolio || [],
      });
    });
  }, []);

  const onPickPortfolio = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const current = form.portfolio || [];
    const room = MAX_PORTFOLIO - current.length;
    if (room <= 0) { toast.error(`En fazla ${MAX_PORTFOLIO} görsel ekleyebilirsiniz`); return; }
    setUploadingPortfolio(true);
    try {
      const picked = files.filter((f) => f.type.startsWith('image/')).slice(0, room);
      const added = [];
      for (const f of picked) {
        const dataUrl = await readFileDataUrl(f);
        added.push(await resizeDataUrl(dataUrl));
      }
      setForm((prev) => ({ ...prev, portfolio: [...(prev.portfolio || []), ...added] }));
      toast.success('Görsel(ler) eklendi, kaydetmeyi unutmayın');
    } catch {
      toast.error('Görsel yüklenemedi');
    } finally { setUploadingPortfolio(false); }
  };

  const removePortfolio = (idx) => {
    setForm((prev) => ({ ...prev, portfolio: (prev.portfolio || []).filter((_, i) => i !== idx) }));
  };

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Lütfen bir resim dosyası seçin'); return; }
    try {
      const dataUrl = await readFileDataUrl(file);
      setCropSrc(dataUrl);
    } catch { toast.error('Fotoğraf okunamadı'); }
  };

  const onCropped = (dataUrl) => {
    setForm((f) => ({ ...f, profilePhoto: dataUrl }));
    setCropSrc(null);
    toast.success('Fotoğraf hazır, kaydetmeyi unutmayın');
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/architect/profile', form);
      setArch(r.data);
      toast.success('Profil güncellendi');
    } catch {
      toast.error('Güncelleme başarısız');
    } finally { setSaving(false); }
  };

  if (!arch) return <div className="p-6 text-ink/40 text-sm">Yükleniyor...</div>;

  const filled = COMPLETE_FIELDS.filter((k) => (form[k] || '').toString().trim().length > 0).length;
  const pct = Math.round((filled / COMPLETE_FIELDS.length) * 100);

  return (
    <div className="px-5 py-6 fade-up" data-testid="profile-page">
      <h1 className="font-serif text-2xl text-ink mb-5">Profil</h1>

      {/* Identity card with avatar */}
      <Card className="p-5 mb-4">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            data-testid="profile-photo-btn"
            className="relative w-20 h-20 rounded-full border border-line overflow-hidden shrink-0 active:scale-95 transition-transform bg-surface-2"
          >
            {form.profilePhoto
              ? <img src={form.profilePhoto} alt="" className="w-full h-full object-cover" data-testid="profile-photo-img" />
              : <span className="w-full h-full flex items-center justify-center"><User strokeWidth={1.25} className="w-8 h-8 text-ink-faint" /></span>}
            <span className="absolute bottom-0 inset-x-0 bg-ink/70 py-1 flex items-center justify-center">
              <Camera strokeWidth={1.5} className="w-3.5 h-3.5 text-ground" />
            </span>
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={onPick} className="hidden" data-testid="profile-photo-input" />
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className="font-serif text-xl text-ink truncate">{arch.name}</p>
              <TierBadge tier={arch.tier} />
            </div>
            <p className="text-sm text-ink/50 truncate">{arch.email}</p>
            <div className="flex items-center gap-2 mt-2">
              <StatusBadge status={arch.status} />
              {arch.approved
                ? <span className="inline-flex items-center gap-1 text-xs text-pos"><BadgeCheck strokeWidth={1.5} className="w-3.5 h-3.5" /> Onaylı</span>
                : <span className="text-xs text-gold">Onay bekliyor</span>}
            </div>
          </div>
        </div>
        {/* Completeness */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-1.5">
            <span className="mini-label">Profil Tamlığı</span>
            <span className="text-xs font-semibold text-ink tabular" data-testid="profile-completion">%{pct}</span>
          </div>
          <Progress value={pct} />
          {pct < 100 && <p className="text-[11px] text-ink-soft mt-1.5">Eksik alanları doldurarak profilini tamamla.</p>}
        </div>
        <p className="text-xs text-ink/40 mt-3">Üyelik: {formatDate(arch.joinDate)}</p>
      </Card>

      {/* Personal info */}
      <p className="mini-label mb-2 px-1">Kişisel Bilgiler</p>
      <Card className="p-5 mb-4 space-y-4">
        <Field label="Ad Soyad" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="profile-name" />
        <Field label="Telefon" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="profile-phone" />
        <div className="grid grid-cols-2 gap-3">
          <SelectField label="Cinsiyet" value={form.gender} onChange={(v) => setForm({ ...form, gender: v })} testid="profile-gender"
            options={['', 'Kadın', 'Erkek', 'Belirtmek istemiyorum']} placeholder="Seçin" />
          <SelectField label="Medeni durum" value={form.maritalStatus} onChange={(v) => setForm({ ...form, maritalStatus: v })} testid="profile-marital"
            options={['', 'Evli', 'Bekar']} placeholder="Seçin" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Doğum tarihi" type="date" value={form.birthDate} onChange={(v) => setForm({ ...form, birthDate: v })} testid="profile-birthdate" />
          <Field label="Şehir" value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="profile-city" />
        </div>
        <Field label="Yaşadığı bölge / semt" value={form.region} onChange={(v) => setForm({ ...form, region: v })} testid="profile-region" placeholder="Örn. Kadıköy" />
      </Card>

      {/* Education */}
      <p className="mini-label mb-2 px-1">Eğitim / Mezuniyet</p>
      <Card className="p-5 mb-4 space-y-4">
        <Field label="Üniversite" value={form.university} onChange={(v) => setForm({ ...form, university: v })} testid="profile-university" placeholder="Örn. İstanbul Teknik Üniversitesi" icon={GraduationCap} />
        <SelectField label="Bölüm" value={form.department} onChange={(v) => setForm({ ...form, department: v })} testid="profile-department"
          options={['', 'Mimarlık', 'İç Mimarlık', 'İç Mimarlık ve Çevre Tasarımı', 'Peyzaj Mimarlığı', 'Şehir ve Bölge Planlama', 'Diğer']} placeholder="Seçin" />
      </Card>

      {/* Portfolio gallery */}
      <p className="mini-label mb-2 px-1">Portföy</p>
      <Card className="p-5 mb-4">
        <p className="text-xs text-ink-soft mb-3">Tamamladığınız projelerden görseller ekleyin (en fazla {MAX_PORTFOLIO}).</p>
        <div className="grid grid-cols-3 gap-2.5" data-testid="portfolio-grid">
          {(form.portfolio || []).map((src, i) => (
            <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-line bg-surface-2 group" data-testid={`portfolio-item-${i}`}>
              <img src={src} alt={`Portföy ${i + 1}`} className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removePortfolio(i)}
                data-testid={`portfolio-remove-${i}`}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-ink/70 text-ground flex items-center justify-center active:scale-90 transition-transform"
                aria-label="Görseli kaldır"
              >
                <X strokeWidth={2} className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          {(form.portfolio || []).length < MAX_PORTFOLIO && (
            <button
              type="button"
              onClick={() => portfolioRef.current?.click()}
              disabled={uploadingPortfolio}
              data-testid="portfolio-add-btn"
              className="aspect-square rounded-xl border border-dashed border-line bg-ground flex flex-col items-center justify-center gap-1 text-ink-faint active:scale-95 transition-transform disabled:opacity-50"
            >
              <Plus strokeWidth={1.5} className="w-6 h-6" />
              <span className="text-[10px] font-medium">{uploadingPortfolio ? 'Yükleniyor' : 'Ekle'}</span>
            </button>
          )}
        </div>
        <input ref={portfolioRef} type="file" accept="image/*" multiple onChange={onPickPortfolio} className="hidden" data-testid="portfolio-input" />
      </Card>

      {/* Professional / payment info */}
      <p className="mini-label mb-2 px-1">Mesleki & Ödeme Bilgileri</p>
      <Card className="p-5 mb-4 space-y-4">
        <Field label="IBAN" value={form.iban} onChange={(v) => setForm({ ...form, iban: v })} testid="profile-iban" />
        <Field label="Mesleki doğrulama (Oda sicil no)" value={form.verification} onChange={(v) => setForm({ ...form, verification: v })} testid="profile-verification" />
        <button onClick={save} disabled={saving} data-testid="profile-save-btn" className="w-full bg-ink text-ground rounded-xl py-3 text-sm font-semibold active:scale-95 transition-transform disabled:opacity-50">
          {saving ? 'Kaydediliyor...' : 'Kaydet'}
        </button>
      </Card>

      {/* Notifications */}
      <div className="mb-4">
        <PushToggle />
      </div>

      {/* Links */}
      <div className="space-y-2 mb-4">
        <LinkRow icon={Users} label="Davet & Kulüp Kartı" onClick={() => navigate('/app/davet')} testid="profile-invite-link" />
        <LinkRow icon={Bell} label="Etkinlikler & Bildirimler" onClick={() => navigate('/app/etkinlikler')} testid="profile-events-link" />
        <LinkRow icon={ShieldCheck} label="Ödüller & Seviye" onClick={() => navigate('/app/oduller')} testid="profile-rewards-link" />
      </div>

      <button onClick={logout} data-testid="logout-btn" className="w-full flex items-center justify-center gap-2 border border-redx/30 text-redx rounded-xl py-3 text-sm font-medium active:scale-95 transition-transform">
        <LogOut strokeWidth={1.25} className="w-4 h-4" /> Çıkış Yap
      </button>

      {cropSrc && <CropModal src={cropSrc} onCancel={() => setCropSrc(null)} onApply={onCropped} />}
    </div>
  );
}

const Field = ({ label, value, onChange, testid, type = 'text', placeholder, icon: Icon }) => (
  <div>
    <label className="text-xs text-ink/50 block mb-1.5">{label}</label>
    <div className="relative">
      {Icon && <Icon strokeWidth={1.25} className="w-4 h-4 text-ink-faint absolute left-3 top-1/2 -translate-y-1/2" />}
      <input
        data-testid={testid}
        type={type}
        value={value || ''}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full bg-ground border border-line rounded-lg py-2.5 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold transition-colors ${Icon ? 'pl-9 pr-3' : 'px-3'}`}
      />
    </div>
  </div>
);

const SelectField = ({ label, value, onChange, testid, options, placeholder }) => (
  <div>
    <label className="text-xs text-ink/50 block mb-1.5">{label}</label>
    <select
      data-testid={testid}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-ground border border-line rounded-lg px-3 py-2.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold transition-colors appearance-none"
    >
      {options.map((o) => (
        <option key={o} value={o}>{o === '' ? (placeholder || 'Seçin') : o}</option>
      ))}
    </select>
  </div>
);

const LinkRow = ({ icon: Icon, label, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} className="w-full flex items-center justify-between bg-surface border border-line rounded-2xl p-4 active:scale-95 transition-transform">
    <span className="flex items-center gap-3">
      <Icon strokeWidth={1.25} className="w-5 h-5 text-gold" />
      <span className="text-sm font-medium text-ink">{label}</span>
    </span>
    <ChevronRight strokeWidth={1.25} className="w-4 h-4 text-ink/30" />
  </button>
);

// Square crop modal with pan + zoom, exports a 256px JPEG data URL
const VIEW = 264;
const OUT = 256;
function CropModal({ src, onCancel, onApply }) {
  const imgRef = useRef(null);
  const [nat, setNat] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const drag = useRef(null);

  const onLoad = (e) => {
    const iw = e.target.naturalWidth, ih = e.target.naturalHeight;
    setNat({ iw, ih });
    setPos({ x: 0, y: 0 });
    setZoom(1);
  };

  const baseScale = nat ? VIEW / Math.min(nat.iw, nat.ih) : 1;
  const scale = baseScale * zoom;
  const dw = nat ? nat.iw * scale : 0;
  const dh = nat ? nat.ih * scale : 0;

  const clamp = (x, y) => ({
    x: Math.min(0, Math.max(VIEW - dw, x)),
    y: Math.min(0, Math.max(VIEW - dh, y)),
  });

  const start = (cx, cy) => { drag.current = { cx, cy, ox: pos.x, oy: pos.y }; };
  const move = (cx, cy) => {
    if (!drag.current) return;
    setPos(clamp(drag.current.ox + (cx - drag.current.cx), drag.current.oy + (cy - drag.current.cy)));
  };
  const end = () => { drag.current = null; };

  const apply = () => {
    if (!nat) return;
    const cp = clamp(pos.x, pos.y);
    const canvas = document.createElement('canvas');
    canvas.width = OUT; canvas.height = OUT;
    const ctx = canvas.getContext('2d');
    const sSize = VIEW / scale;
    const sx = -cp.x / scale;
    const sy = -cp.y / scale;
    ctx.drawImage(imgRef.current, sx, sy, sSize, sSize, 0, 0, OUT, OUT);
    onApply(canvas.toDataURL('image/jpeg', 0.85));
  };

  const p = clamp(pos.x, pos.y);

  return (
    <div className="fixed inset-0 z-50 bg-chrome/70 backdrop-blur-sm flex items-center justify-center p-5" role="dialog" aria-modal="true" aria-label="Fotoğrafı kırp" data-testid="crop-modal">
      <div className="bg-surface border border-line rounded-2xl p-5 w-full max-w-sm">
        <p className="font-serif text-lg text-ink mb-1">Fotoğrafı kırp</p>
        <p className="text-xs text-ink-soft mb-4">Sürükleyerek konumlandır, yakınlaştırmak için kaydır.</p>
        <div
          className="relative mx-auto rounded-full overflow-hidden border border-line touch-none cursor-move bg-surface-2"
          style={{ width: VIEW, height: VIEW }}
          onMouseDown={(e) => start(e.clientX, e.clientY)}
          onMouseMove={(e) => move(e.clientX, e.clientY)}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={(e) => start(e.touches[0].clientX, e.touches[0].clientY)}
          onTouchMove={(e) => move(e.touches[0].clientX, e.touches[0].clientY)}
          onTouchEnd={end}
        >
          <img
            ref={imgRef}
            src={src}
            alt=""
            onLoad={onLoad}
            draggable={false}
            style={{ position: 'absolute', left: p.x, top: p.y, width: dw, height: dh, maxWidth: 'none' }}
          />
        </div>
        <input
          type="range" min="1" max="3" step="0.01" value={zoom}
          onChange={(e) => { const z = parseFloat(e.target.value); setZoom(z); setPos((pp) => pp); }}
          data-testid="crop-zoom"
          className="w-full mt-4 accent-gold"
        />
        <div className="grid grid-cols-2 gap-3 mt-4">
          <button onClick={onCancel} data-testid="crop-cancel" className="border border-line text-ink rounded-xl py-2.5 text-sm font-medium active:scale-95 transition-transform">İptal</button>
          <button onClick={apply} data-testid="crop-apply" className="bg-ink text-ground rounded-xl py-2.5 text-sm font-semibold active:scale-95 transition-transform">Uygula</button>
        </div>
      </div>
    </div>
  );
}
